import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Table, 
  Button, 
  Badge, 
  Spinner, 
  Alert, 
  Row, 
  Col, 
  Card,
  Pagination,
  Modal,
  Form
} from 'react-bootstrap';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import './Recordings.css';

const Recordings = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [limit] = useState(50);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const modalRef = useRef(false);
  const [audioDuration, setAudioDuration] = useState(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioLoaded, setAudioLoaded] = useState(false);
  
  // AI Feedback form state
  const [formData, setFormData] = useState({
    question: '',
    wrong_answer: '',
    right_answer: '',
    brand_name: 'hive'
  });
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [clickedAiText, setClickedAiText] = useState('');

  const { refreshToken, isRefreshing } = useAuth();

  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/ai/sync-status');
      const syncData = response.data;
      
      // Format the last sync date for display
      if (syncData.last_sync_at) {
        const lastSyncDate = new Date(syncData.last_sync_at);
        setLastSyncTime(lastSyncDate.toLocaleString());
      } else {
        setLastSyncTime(null);
      }
      
      // Don't update total recordings count here - let fetchRecordings handle it
      // This ensures consistency between dashboard and table
      
    } catch (error) {
      console.error('Error fetching sync status:', error);
      // Don't set error for sync status, just log it
    }
  }, []);

  const fetchRecordings = useCallback(async (autoSync = false) => {
    try {
      setLoading(true);
      setError('');
      
      const offset = (currentPage - 1) * limit;
      
      // Only sync with VAPI if explicitly requested (refresh button)
      if (autoSync) {
        await syncWithVAPI();
      }
      
              const response = await api.get(`/api/ai/recordings?limit=${limit}&offset=${offset}&sync_from_vapi=false`);
      
      console.log('📊 Fetched recordings:', response.data.recordings.length);
      console.log('📊 First recording URL:', response.data.recordings[0]?.recordingUrl);
      
      setRecordings(response.data.recordings);
      setTotalRecordings(response.data.total);
      setTotalPages(Math.ceil(response.data.total / limit));
    } catch (error) {
      console.error('Error fetching recordings:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        setError('Authentication failed. Please refresh the page to login again.');
        // Try to refresh token
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry the request
          fetchRecordings(autoSync);
          return;
        }
      } else {
        setError('Failed to fetch recordings: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit]);

  // Handle AI transcript click to replace Wrong Answer field and open form
  const handleTranscriptClick = useCallback((event) => {
    if (event.target.classList.contains('transcript-ai')) {
      const aiText = event.target.innerText;
      
      // Store the clicked text and replace the wrong answer field
      setClickedAiText(aiText);
      setFormData(prev => ({
        ...prev,
        wrong_answer: aiText
      }));
      
      // Automatically open the feedback form on first click
      if (!showFeedbackForm) {
        setShowFeedbackForm(true);
      }
    }
  }, [showFeedbackForm]);

  useEffect(() => {
    fetchRecordings(false); // Don't auto-sync on page load
    fetchSyncStatus(); // Fetch sync status
  }, [fetchRecordings, fetchSyncStatus]);

  // Add event listener for transcript clicks when modal is open
  useEffect(() => {
    if (showPlayer && selectedRecording) {
      const transcriptContainer = document.querySelector('.transcript-content');
      if (transcriptContainer) {
        transcriptContainer.addEventListener('click', handleTranscriptClick);
        
        // Cleanup function
        return () => {
          transcriptContainer.removeEventListener('click', handleTranscriptClick);
        };
      }
    }
  }, [showPlayer, selectedRecording, handleTranscriptClick]);

  const syncWithVAPI = async () => {
    try {
      setSyncing(true);
      console.log('🔄 Starting AWS S3 sync for new recordings...');
      
      const response = await api.post('/api/aws/sync-recordings', {
        prefix: 'vapi/call/'
      });
      const result = response.data;
      
      const newRecordings = result.new_recordings_created || 0;
      const updatedRecordings = result.existing_recordings_updated || 0;
      const filesProcessed = result.files_processed || 0;
      
      if (filesProcessed > 0) {
        console.log(`✅ AWS S3 sync completed: ${newRecordings} new, ${updatedRecordings} updated recordings from ${filesProcessed} files`);
        // Refresh sync status to get updated last sync time
        await fetchSyncStatus();
      } else {
        console.log('ℹ️ No new recordings found in AWS S3');
      }
      
      return result;
    } catch (error) {
      console.error('❌ AWS S3 sync failed:', error);
      if (error.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          return syncWithVAPI();
        }
      }
      throw error;
    } finally {
      setSyncing(false);
    }
  };

  const handleRefresh = async () => {
    await fetchRecordings(true); // Enable sync only when refresh button is pressed
  };

  const handlePlayRecording = async (recording) => {
    try {
      console.log('🎵 Play button clicked for recording:', recording.id);
      console.log('🎵 Recording URL:', recording.recordingUrl);
      setLoadingUrl(true);
      setSelectedRecording(recording);
      
      // Use the recording URL directly from the recording data
      if (recording.recordingUrl) {
        console.log('🎵 Setting recording URL:', recording.recordingUrl);
        modalRef.current = true;
        setShowPlayer(true);
        console.log('🎵 Modal should now be open, showPlayer:', true, 'modalRef:', modalRef.current);
      } else {
        console.log('❌ No recording URL available');
        setError('No recording URL available for this recording');
      }
    } catch (error) {
      console.error('❌ Error in handlePlayRecording:', error);
      setError('Failed to get recording URL: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingUrl(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTranscript = (transcript) => {
    if (!transcript) return '';
    
    // Split the transcript by "User:" and "AI:" to separate the conversation
    const parts = transcript.split(/(?=User:|AI:)/);
    
    return parts.map((part, index) => {
      const trimmedPart = part.trim();
      if (!trimmedPart) return null;
      
      if (trimmedPart.startsWith('User:')) {
        // Replace "User:" with "Caller:" and style it
        const callerText = trimmedPart.substring(5).trim();
        return `<span class="transcript-caller">Caller: ${callerText}</span>`;
      } else if (trimmedPart.startsWith('AI:')) {
        // Keep "AI:" and style it
        const aiText = trimmedPart.substring(3).trim();
        return `<span class="transcript-ai">AI: ${aiText}</span>`;
      } else {
        // If it doesn't start with either, assume it's AI speaking
        return `<span class="transcript-ai">AI: ${trimmedPart}</span>`;
      }
    }).filter(Boolean).join('<br><br>');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': { variant: 'success', text: 'Completed' },
      'in-progress': { variant: 'warning', text: 'In Progress' },
      'failed': { variant: 'danger', text: 'Failed' },
      'cancelled': { variant: 'secondary', text: 'Cancelled' },
      'ended': { variant: 'success', text: 'Ended' }
    };
    
    const config = statusConfig[status] || { variant: 'info', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/ai-feedback', formData);
      console.log('Feedback submitted:', response.data);
      
      // Reset form
      setFormData({
        question: '',
        wrong_answer: '',
        right_answer: '',
        brand_name: 'hive'
      });
      setShowFeedbackForm(false);
      setClickedAiText('');
      
      // Show success message (you can add a toast notification here)
      alert('Feedback submitted successfully!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  if (loading && currentPage === 1) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading recordings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <Row>
          <Col>
            <h2>Call Recordings</h2>
            <p className="text-muted mb-0">
              View and play video call recordings from VAPI
              {lastSyncTime && (
                <span className="ms-2 text-success">
                  • Last synced: {lastSyncTime}
                </span>
              )}
            </p>
          </Col>
        </Row>
      </div>

      <div className="page-content">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {/* Sync Status Dashboard */}
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Sync Status</h5>
            <hr className="mb-3" />
            <Row>
              <Col md={3} className="text-center">
                <div className="mb-2">
                  <h3 className="mb-0 text-dark">{totalRecordings}</h3>
                  <small className="text-muted">Total Recordings</small>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="mb-2">
                  <h3 className="mb-0 text-dark">{recordings.length}</h3>
                  <small className="text-muted">Recent Recordings</small>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="mb-2">
                  <h6 className="mb-0 text-dark">
                    {lastSyncTime ? (
                      <span className="text-dark">{lastSyncTime}</span>
                    ) : (
                      <span className="text-muted">Never</span>
                    )}
                  </h6>
                  <small className="text-muted">Last Sync Date</small>
                </div>
              </Col>
              <Col md={3} className="text-center">
                <div className="mb-2">
                  <h6 className="mb-0 text-dark">vapi-parklane-test</h6>
                  <small className="text-muted">Bucket Name</small>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              AWS S3 Call Recordings ({totalRecordings})
              {syncing && (
                <span className="ms-2">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span className="ms-1 text-muted">Syncing...</span>
                </span>
              )}
            </h5>
            <div>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleRefresh}
                disabled={loading || syncing || isRefreshing}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Sync Call Recordings'
                )}
              </Button>
            </div>
          </div>

          <Table responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Squad</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Started</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recordings.map((recording) => (
                <tr key={recording.id}>
                  <td>
                    <code>{recording.id}</code>
                  </td>
                  <td>
                    <div>
                      <strong>{recording.squad_name || 'Unknown Squad'}</strong>
                      <br />
                      <small className="text-muted">
                        {Array.isArray(recording.squad_id) 
                          ? recording.squad_id[0] 
                          : recording.squad_id || 'No Squad ID'}
                      </small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>{recording.customer?.name || 'Unknown'}</strong>
                      <br />
                      <small className="text-muted">
                        {recording.customer?.phoneNumber || 'No phone'}
                      </small>
                    </div>
                  </td>
                  <td>{getStatusBadge(recording.status)}</td>
                  <td>{formatDuration(recording.duration)}</td>
                  <td>{formatDate(recording.startedAt)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handlePlayRecording(recording)}
                      disabled={!recording.recordingUrl}
                    >
                      {loadingUrl && selectedRecording?.id === recording.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Play'
                      )}
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => {
                        setSelectedRecording(recording);
                        setShowPlayer(true);
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First 
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
      </div>

      {/* Recording Player Modal */}
      {console.log('🎵 Modal render check - showPlayer:', showPlayer, 'modalRef:', modalRef.current, 'selectedRecording:', selectedRecording?.id)}
      <Modal show={showPlayer || modalRef.current} onHide={() => {
        setShowPlayer(false);
        modalRef.current = false;
      }} size="lg" style={{ zIndex: 9999 }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedRecording?.customer?.name || 'Unknown'} - Call Recording
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecording && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Customer:</strong> {selectedRecording.customer?.name || 'Unknown'}
                </Col>
                <Col md={6}>
                  <strong>Phone:</strong> {selectedRecording.customer?.phoneNumber || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Status:</strong> {getStatusBadge(selectedRecording.status)}
                </Col>
                <Col md={6}>
                  <strong>Duration:</strong> {formatDuration(selectedRecording.duration)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Started:</strong> {formatDate(selectedRecording.startedAt)}
                </Col>
                <Col md={6}>
                  <strong>Ended:</strong> {selectedRecording.endedAt ? formatDate(selectedRecording.endedAt) : 'N/A'}
                </Col>
              </Row>
              
              {selectedRecording?.recordingUrl && (
                <div className="mt-3">
                  <h6>Recording:</h6>
                  <audio 
                    controls 
                    className="w-100"
                    onLoadedMetadata={(e) => {
                      console.log('🎵 Audio loaded, duration:', e.target.duration);
                      setAudioDuration(e.target.duration);
                      setAudioLoaded(true);
                    }}
                    onTimeUpdate={(e) => {
                      console.log('🎵 Audio time update:', e.target.currentTime, '/', e.target.duration);
                      setAudioCurrentTime(e.target.currentTime);
                    }}
                    onError={(e) => {
                      console.error('🎵 Audio error:', e.target.error);
                      setAudioLoaded(false);
                    }}
                    preload="metadata"
                  >
                    <source src={`/api/ai/recordings/${selectedRecording.id}/audio`} type="audio/wav" />
                    Your browser does not support the audio element.
                  </audio>
                  <div className="mt-2">
                    <small className="text-muted">
                      Duration: {audioLoaded && audioDuration ? formatDuration(audioDuration) : 
                               selectedRecording.duration ? formatDuration(selectedRecording.duration) : 'Loading...'}
                      {audioLoaded && audioDuration && (
                        <span className="ms-2">
                          | Current: {formatDuration(audioCurrentTime)}
                        </span>
                      )}
                    </small>
                  </div>
                </div>
              )}
              
              {selectedRecording.transcript && (
                <div className="mt-3">
                  <h6>Transcript:</h6>
                  <div className="transcript-container">
                    <div 
                      className="transcript-content"
                      dangerouslySetInnerHTML={{ __html: formatTranscript(selectedRecording.transcript) }}
                    />
                  </div>
                  <small className="text-muted">
                    💡 Click on any AI message (orange) to auto-fill the feedback form and open it
                  </small>
                </div>
              )}

              {/* AI Feedback Form */}
              {showFeedbackForm && (
                <div className="mt-4">
                  <hr />
                  <h6>AI Feedback Form</h6>
                  <Form onSubmit={handleFormSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Question *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="question"
                        value={formData.question}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter the question..."
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Wrong Answer *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="wrong_answer"
                        value={formData.wrong_answer}
                        onChange={handleInputChange}
                        required
                        placeholder="Click on AI transcript messages above to auto-fill..."
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Right Answer *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="right_answer"
                        value={formData.right_answer}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter the correct answer..."
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Brand Name</Form.Label>
                      <Form.Select
                        name="brand_name"
                        value={formData.brand_name}
                        onChange={handleInputChange}
                      >
                        <option value="hive">Hive</option>
                        <option value="iconinc">Iconinc</option>
                        <option value="rentinc">Rentinc</option>
                        <option value="plp">PLP</option>
                        <option value="roomzzz">Roomzzz</option>
                      </Form.Select>
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button variant="primary" type="submit">
                        Submit Feedback
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        type="button"
                        onClick={() => {
                          setFormData({
                            question: '',
                            wrong_answer: '',
                            right_answer: '',
                            brand_name: 'hive'
                          });
                          setClickedAiText('');
                        }}
                      >
                        Clear Form
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button 
              variant="outline-primary" 
              onClick={() => setShowFeedbackForm(!showFeedbackForm)}
            >
              {showFeedbackForm ? 'Hide' : 'Add'} AI Feedback
            </Button>
            <Button variant="secondary" onClick={() => {
              setShowPlayer(false);
              modalRef.current = false;
              setShowFeedbackForm(false);
            }}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Recordings; 